import { useCallback, useEffect, useRef, useState } from "react";
const HERZSCHLAG_VORGABE_MS = 30_000;
// Nachrüstauftrag N2: halb so schnell wie der Herzschlag — schnell genug, dass ein
// freiwerdender Datensatz zeitnah bemerkt wird, langsam genug, dass es nichts belastet.
const FREMD_BEOBACHTUNG_VORGABE_MS = 15_000;
/**
 * Fixrunde 2, Fund 3: `ApiError` aus `packages/api-client` legt den Halter bei einer 423
 * verschachtelt an (`locked: { lockedBy, since }`, siehe dort `wirfBeiFehler`) — nicht flach.
 * `ui` darf `api-client` nicht kennen, muss die Gestalt seines Fehlers aber trotzdem treffen,
 * sonst zeigt FREMD dauerhaft "unbekannt" statt eines echten Namens (genau der Mangel, den
 * die vorige Fixrunde eigentlich beheben sollte — die Formen trafen sich nur nicht). Der
 * Haken zieht sich deshalb auf die Gestalt des Clients, nicht umgekehrt: ein Adapter in
 * Aufgabe 16 müsste sonst extra umformen, eine weitere Stelle, an der der Mangel wieder
 * entstehen könnte.
 */
function istGesperrtDurchAndere(fehler) {
    return typeof fehler === "object" && fehler !== null
        && "status" in fehler && fehler.status === 423;
}
function alsFehlermeldung(fehler) {
    return fehler instanceof Error ? fehler.message : "Die Sperre konnte nicht geholt werden.";
}
/**
 * Hält eine Bearbeitungssperre, solange ein Datensatz aktiv in Bearbeitung ist. Die Sperre
 * ist Höflichkeit, nicht Garantie — sie verhindert nur, dass jemand umsonst tippt, während
 * ein anderer denselben Satz bearbeitet. Gegen verlorene Änderungen schützt allein der
 * Versionsvergleich beim Speichern (siehe Aufgabe 8); dieser Haken bildet das nicht nach.
 *
 * `ui` ist Blatt und darf `api-client` nicht kennen (siehe `scripts/check-boundaries.mjs`) —
 * die drei Aufrufe kommen deshalb als `dienst` von außen herein. Das macht den Haken
 * nebenbei ohne Netzwerk testbar.
 */
export function useRecordLock(opts) {
    const { ziel, aktiv, herzschlagMs = HERZSCHLAG_VORGABE_MS, beobachtungMs = FREMD_BEOBACHTUNG_VORGABE_MS, } = opts;
    const [zustand, setZustand] = useState({ art: "AUS" });
    // Wie in useFocusTrap (Entscheidung AG, Paket 1): der Aufrufer erzeugt `dienst` in aller
    // Regel bei jedem Neuzeichnen neu. Stünde `dienst` in den Abhängigkeiten des Effekts
    // unten, holte jeder Render eine neue Sperre. Die Zuweisung während des Renderns hält die
    // Referenz aktuell, ohne dass ihre (instabile) Identität den Effekt erneut anlaufen lässt.
    // Fixrunde 2, Fund 9: "dienstRef" statt "onDienstRef" — das "on…"-Präfix gehört zu einem
    // Rückrufhaken (aus useFocusTraps onCloseRef übernommen), hier steckt aber ein Dienstobjekt
    // dahinter, kein Rückruf.
    const dienstRef = useRef(opts.dienst);
    dienstRef.current = opts.dienst;
    // Nachrüstauftrag N2: `uebernehmen()` muss über Neuzeichnungen hinweg dieselbe Identität
    // behalten (der Aufrufer könnte sie in eine Abhängigkeitsliste stecken), aber intern immer
    // die Übernahme-Aktion DES AKTUELLEN Effektlaufs auslösen — dieselbe Ref-Konstruktion wie
    // bei dienstRef, nur für eine Funktion statt für den Dienst selbst. Solange kein aktiver
    // Effektlauf existiert (aktiv:false, kein ziel), ist sie ein wirkungsloses No-op.
    const uebernehmenRef = useRef(async () => { });
    const uebernehmen = useCallback(() => uebernehmenRef.current(), []);
    const entityType = ziel?.entityType ?? null;
    const entityId = ziel?.entityId ?? null;
    useEffect(() => {
        if (!aktiv || entityType === null || entityId === null) {
            setZustand({ art: "AUS" });
            uebernehmenRef.current = async () => { };
            return;
        }
        const zielFuerEffekt = { entityType, entityId };
        // Marke, die anzeigt, dass dieser Lauf des Effekts beendet (abgemeldet) ist — eine
        // noch fliegende Anfrage darf beim Eintreffen dann keinen Zustand mehr setzen. Ohne
        // diese Marke bliebe eine Sperre nach dem Verlassen der Ansicht unter Umständen
        // "GEHALTEN" in einer Komponente, die längst nicht mehr existiert.
        //
        // Konkret verhindert wird: ein Unmount, während acquire() noch fliegt. Läuft acquire()
        // danach durch, würde ohne diese Marke setInterval() erst NACH dem Unmount starten —
        // release() aus dem Aufräumer ist dann längst gelaufen, und der Herzschlag hielte die
        // Sperre unbemerkt am Leben (mit fake timers + herzschlagMs:100 real nachgemessen:
        // 5 refresh()-Aufrufe statt 0). Bewusst OHNE eigenen Dauertest in useRecordLock.test.tsx:
        // der Test bräuchte ein absichtlich verzögertes acquire()-Promise, was ihn schwerer zu
        // pflegen machte als diese eine Zeile, die er absichert. Bitte nicht als totes Beiwerk
        // beim Aufräumen entfernen.
        let abgemeldet = false;
        // Ein einziges Intervallfeld für BEIDE periodischen Vorgänge (Herzschlag und
        // Nachrüstauftrag N2s FREMD-Beobachtung) — die beiden schließen sich gegenseitig aus
        // (GEHALTEN hat einen Herzschlag, FREMD eine Beobachtung, nie beides gleichzeitig), ein
        // gemeinsames Feld macht das beim Wechsel unmöglich zu vergessen.
        let intervall = null;
        // Fixrunde 2, Fund 5: nur freigeben, was tatsächlich erworben wurde. Ohne diesen Riegel
        // rief der Aufräumer release() auch dann, wenn acquire() nie erfolgreich war (z. B. weil
        // die Sperre von Anfang an FREMD war) — serverseitig harmlos (releaseLock ist auf die
        // eigene user_id eingeschränkt), aber eine überflüssige Anfrage, die nur ins Leere läuft.
        let sperreGehalten = false;
        // Fixrunde 2, Fund 1: merkt sich, ob der letzte Herzschlag mit einem technischen Fehler
        // (nicht 423) fehlschlug, damit ein darauffolgender ERFOLGREICHER Herzschlag den Zustand
        // aus FEHLER zurück nach GEHALTEN führen kann. Ohne diese Marke müsste jeder erfolgreiche
        // Herzschlag erneut setZustand(GEHALTEN) aufrufen (unnötige Neuzeichnungen alle
        // herzschlagMs), oder FEHLER bliebe eine Sackgasse, obwohl die Sperre längst wieder hält.
        let letzterHerzschlagFehlgeschlagen = false;
        setZustand({ art: "HOLT" });
        /** Übergang nach GEHALTEN, gleich auf welchem Weg (Ersterwerb, Herzschlag-Heilung würde
         *  hier NICHT durchlaufen — die bleibt in herzschlagen() speziell, weil sie keinen neuen
         *  Herzschlag-Takt braucht). Räumt ein laufendes Intervall (Beobachtung ODER, im
         *  Normalfall nicht vorhanden, einen alten Herzschlag) weg und setzt frisch auf. */
        function zuGehaltenWechseln() {
            if (intervall !== null) {
                clearInterval(intervall);
                intervall = null;
            }
            sperreGehalten = true;
            letzterHerzschlagFehlgeschlagen = false;
            setZustand({ art: "GEHALTEN" });
            intervall = setInterval(() => { void herzschlagen(); }, herzschlagMs);
        }
        /** Übergang nach FREMD, gleich auf welchem Weg (Ersterwerb schlägt fehl, Herzschlag
         *  verliert die Sperre, Übernahme scheitert). Räumt ein laufendes Intervall weg (den
         *  Herzschlag, falls die Sperre gerade erst verloren ging) und startet die stille
         *  Beobachtung aus Nachrüstauftrag N2 — sonst bliebe FREMD eine Endstation ohne
         *  Neuversuch, und "bekommt die Bearbeitung angeboten, sobald die Sperre fällt" aus der
         *  Spezifikation hätte keinen Baustein. */
        function zuFremdWechseln(fehler) {
            if (intervall !== null) {
                clearInterval(intervall);
                intervall = null;
            }
            sperreGehalten = false;
            setZustand({
                art: "FREMD",
                von: fehler.locked?.lockedBy ?? "unbekannt",
                seit: fehler.locked?.since ?? "",
            });
            intervall = setInterval(() => { void beobachten(); }, beobachtungMs);
        }
        async function holen() {
            try {
                const sperre = await dienstRef.current.acquire(zielFuerEffekt, undefined);
                if (abgemeldet)
                    return;
                void sperre;
                zuGehaltenWechseln();
            }
            catch (fehler) {
                if (abgemeldet)
                    return;
                if (istGesperrtDurchAndere(fehler)) {
                    zuFremdWechseln(fehler);
                    return;
                }
                setZustand({ art: "FEHLER", meldung: alsFehlermeldung(fehler) });
            }
        }
        async function herzschlagen() {
            try {
                await dienstRef.current.refresh(zielFuerEffekt);
                if (abgemeldet)
                    return;
                // Ein erfolgreicher Herzschlag heilt einen vorherigen technischen Fehlschlag zurück
                // nach GEHALTEN. FREMD ist hier nie der Vorzustand: Sobald FREMD erreicht wird
                // (unten), wird das Intervall sofort gelöscht — herzschlagen() läuft danach nicht
                // mehr an, ein Zurückspringen aus FREMD kann also gar nicht passieren.
                if (letzterHerzschlagFehlgeschlagen) {
                    letzterHerzschlagFehlgeschlagen = false;
                    setZustand({ art: "GEHALTEN" });
                }
            }
            catch (fehler) {
                if (abgemeldet)
                    return;
                // Ein fehlgeschlagener Herzschlag mit 423 führt nach FREMD, NICHT zurück nach HOLT —
                // sonst risse der Haken einem Kollegen, der die Sperre inzwischen übernommen hat,
                // seine Sperre wieder weg (siehe Testfall "verlorene Meldung").
                if (istGesperrtDurchAndere(fehler)) {
                    zuFremdWechseln(fehler);
                    return;
                }
                letzterHerzschlagFehlgeschlagen = true;
                setZustand({ art: "FEHLER", meldung: alsFehlermeldung(fehler) });
            }
        }
        /**
         * Nachrüstauftrag N2, Baustein 1: solange `aktiv` und FREMD anhält, alle `beobachtungMs`
         * ein normaler (force-loser) Erwerbsversuch. Fällt die fremde Sperre, wird sie geräuschlos
         * erworben. Bewusst `acquire` statt einer eigenen `readLock`-Methode — erspart eine
         * Methode und ein Vertragsstück, ein gelungener Erwerb ist ohnehin das Ziel. Ein
         * technischer Fehlschlag (nicht 423) lässt den sichtbaren Zustand unangetastet: ein
         * einzelner Aussetzer soll das FREMD-Band nicht durch eine Störungsmeldung ersetzen —
         * der nächste Takt versucht es erneut.
         */
        async function beobachten() {
            try {
                const sperre = await dienstRef.current.acquire(zielFuerEffekt, undefined);
                if (abgemeldet)
                    return;
                void sperre;
                zuGehaltenWechseln();
            }
            catch (fehler) {
                if (abgemeldet)
                    return;
                if (istGesperrtDurchAndere(fehler)) {
                    // Bleibt FREMD, nur mit frischen Angaben, falls sich der Halter zwischenzeitlich
                    // geändert hat. Das Beobachtungsintervall selbst bleibt unangetastet — dieser
                    // Aufruf IST bereits ein Tick daraus, ein neues aufzusetzen liefe doppelt.
                    setZustand({
                        art: "FREMD",
                        von: fehler.locked?.lockedBy ?? "unbekannt",
                        seit: fehler.locked?.since ?? "",
                    });
                }
            }
        }
        /**
         * Nachrüstauftrag N2, Baustein 2: die von außen aufrufbare Übernahme. Ruft `acquire` MIT
         * `force` — das ist der einzige Unterschied zu `holen`/`beobachten`. Der Haken prüft keine
         * Rolle (siehe Kommentar an `SperrHakenErgebnis`); scheitert die Übernahme trotzdem (423 —
         * z. B. weil ein Dritter im selben Moment force-übernahm), bleibt FREMD mit den frischen
         * Halterdaten, NICHT GEHALTEN — alles andere würde behaupten, die Übernahme sei geglückt.
         */
        async function uebernehmenIntern() {
            if (abgemeldet)
                return;
            try {
                const sperre = await dienstRef.current.acquire(zielFuerEffekt, true);
                if (abgemeldet)
                    return;
                void sperre;
                zuGehaltenWechseln();
            }
            catch (fehler) {
                if (abgemeldet)
                    return;
                if (istGesperrtDurchAndere(fehler)) {
                    zuFremdWechseln(fehler);
                    return;
                }
                setZustand({ art: "FEHLER", meldung: alsFehlermeldung(fehler) });
            }
        }
        uebernehmenRef.current = uebernehmenIntern;
        void holen();
        return () => {
            abgemeldet = true;
            if (intervall !== null)
                clearInterval(intervall);
            uebernehmenRef.current = async () => { };
            if (sperreGehalten) {
                // Bewusst ohne await: die Freigabe beim Verlassen darf das Aufräumen nicht aufhalten.
                // Fixrunde 2, Fund 6: .catch() statt eines nackten void — void unterdrückt nur den
                // Rückgabewert, nicht eine Ablehnung. Scheitert die Freigabe (z. B. Sitzung inzwischen
                // abgelaufen, offline), landete das sonst als unbehandelte Promise-Ablehnung in der
                // Konsole. Ihr Ergebnis interessiert an dieser Stelle ohnehin niemanden mehr — die
                // Sperre ist Höflichkeit, nicht Garantie.
                void dienstRef.current.release(zielFuerEffekt).catch(() => { });
            }
        };
        // dienst bewusst NICHT in dieser Liste — siehe dienstRef oben.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aktiv, entityType, entityId, herzschlagMs, beobachtungMs]);
    return { zustand, uebernehmen };
}
//# sourceMappingURL=useRecordLock.js.map